/**
 * Shared Option B session lifecycle for thin Arcade FE clients.
 * GM remains the rules/state authority; this only owns create/delete + busy.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * @param {object} opts
 * @param {() => Promise<object>} opts.createSession - returns payload with session_id
 * @param {(sessionId: string) => Promise<void>} opts.deleteSession
 */
export function useGmSession({ createSession, deleteSession }) {
    const sessionIdRef = useRef(null);
    const [busy, setBusy] = useState(false);

    const clearSession = useCallback(async () => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        if (sid && typeof deleteSession === 'function') {
            await deleteSession(sid);
        }
    }, [deleteSession]);

    const createFreshSession = useCallback(async (...args) => {
        await clearSession();
        const data = await createSession(...args);
        if (!data?.session_id) {
            throw new Error('GM missing session_id');
        }
        sessionIdRef.current = data.session_id;
        return data;
    }, [clearSession, createSession]);

    useEffect(() => () => {
        const sid = sessionIdRef.current;
        sessionIdRef.current = null;
        if (sid && typeof deleteSession === 'function') {
            deleteSession(sid);
        }
    }, [deleteSession]);

    return {
        sessionIdRef,
        busy,
        setBusy,
        createFreshSession,
        clearSession,
    };
}

export default useGmSession;
